import { useState, FormEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { usersService, type MeProfile } from '../../shared/services/users.service';
import { providersService } from '../../shared/services/providers.service';
import { authService } from '../../shared/services/auth.service';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { validators, normalisePhone } from '../../shared/validators/auth-validators';
import { LocationPicker, type LocationValue } from '../../shared/components/ui/LocationPicker';
import { PasswordField } from '../../shared/components/ui/PasswordField';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import XIcon from '@mui/icons-material/X';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import InputAdornment from '@mui/material/InputAdornment';

type TabKey = 'personal' | 'provider' | 'social' | 'password' | 'delete';

export function AccountSettingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { patchUser } = useAuth();

  const [tab, setTab] = useState<TabKey>('personal');

  const me = useQuery({ queryKey: ['me'], queryFn: () => usersService.getMe() });
  const provider = useQuery({
    queryKey: ['provider', 'me'],
    queryFn: () => providersService.getMine(),
    retry: false,
  });

  if (me.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (me.isError || !me.data) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{translate(me.error)}</Alert>
      </Container>
    );
  }

  const hasProvider = !!provider.data;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {t('account.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('account.subtitle')}
      </Typography>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
        >
          <Tab value="personal" label={t('account.tabs.personal')} />
          {hasProvider && <Tab value="provider" label={t('account.tabs.provider')} />}
          {hasProvider && <Tab value="social" label={t('account.tabs.social')} />}
          <Tab value="password" label={t('account.tabs.password')} />
          <Tab value="delete" label={t('account.tabs.delete')} sx={{ color: 'error.main' }} />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {tab === 'personal' && (
            <PersonalInfoForm
              me={me.data}
              onSaved={(updated) => {
                qc.setQueryData(['me'], updated);
                patchUser({
                  firstName: updated.firstName,
                  lastName: updated.lastName,
                  phoneNumber: updated.phoneNumber ?? undefined,
                  phoneVerified: updated.phoneVerified,
                });
                // SMS phone verification is disabled — phone changes save
                // silently. To re-enable, uncomment the /verify-phone hop
                // below and the matching gate in AuthGuard.
                notify.success(t('account.saved'));
                // if (!updated.phoneVerified) {
                //   navigate('/verify-phone', { replace: true });
                // }
              }}
            />
          )}
          {tab === 'provider' && hasProvider && provider.data && (
            <ProviderInfoForm
              providerData={provider.data}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ['provider', 'me'] });
                notify.success(t('account.saved'));
              }}
            />
          )}
          {tab === 'social' && hasProvider && provider.data && (
            <SocialLinksForm
              providerData={provider.data}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ['provider', 'me'] });
                notify.success(t('account.saved'));
              }}
            />
          )}
          {tab === 'password' && <PasswordForm />}
          {tab === 'delete' && <DeleteAccountForm hasPassword={me.data.hasPassword} />}
        </Box>
      </Paper>
    </Container>
  );
}

// ── Personal info ───────────────────────────────────────────────────────

function PersonalInfoForm({
  me,
  onSaved,
}: {
  me: MeProfile;
  onSaved: (updated: MeProfile) => void;
}) {
  const { t } = useTranslation();
  const { translate } = useApiError();
  const { notify } = useNotification();

  const [form, setForm] = useState({
    firstName: me.firstName ?? '',
    lastName: me.lastName ?? '',
    username: me.username ?? '',
    phoneNumber: me.phoneNumber ?? '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});

  const mutation = useMutation({
    mutationFn: () =>
      usersService.updateMe({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        // Omit when blank so an emptied field leaves the username unchanged
        // (a username can't be cleared — it's always required server-side).
        username: form.username.trim() || undefined,
        phoneNumber: normalisePhone(form.phoneNumber),
      }),
    onSuccess: onSaved,
    onError: (err) => notify.error(translate(err)),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!form.firstName.trim()) next.firstName = t('auth.errors.firstNameRequired');
    if (!form.lastName.trim()) next.lastName = t('auth.errors.lastNameRequired');
    const phoneKey = validators.phone(form.phoneNumber);
    if (phoneKey) next.phoneNumber = t(phoneKey);
    setErrors(next);
    if (Object.keys(next).length === 0) mutation.mutate();
  };

  const phoneChanged = normalisePhone(form.phoneNumber) !== (me.phoneNumber ?? '');

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('auth.firstName')}
            value={form.firstName}
            onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
            required
            fullWidth
            error={!!errors.firstName}
            helperText={errors.firstName}
          />
          <TextField
            label={t('auth.lastName')}
            value={form.lastName}
            onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
            required
            fullWidth
            error={!!errors.lastName}
            helperText={errors.lastName}
          />
        </Stack>

        <TextField
          label={t('auth.email')}
          value={me.email}
          disabled
          fullWidth
          helperText={t('account.personal.emailLocked')}
        />

        <TextField
          label={t('auth.username')}
          value={form.username}
          onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
          fullWidth
          autoComplete="username"
          error={!!errors.username}
          helperText={errors.username || t('account.personal.usernameHint')}
        />

        <TextField
          label={t('auth.phone')}
          value={form.phoneNumber}
          onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))}
          required
          fullWidth
          autoComplete="tel"
          placeholder="+33 6 00 00 00 00"
          error={!!errors.phoneNumber}
          helperText={
            errors.phoneNumber
              ? errors.phoneNumber
              : phoneChanged
              ? t('account.personal.phoneChangeWarning')
              : t('auth.phoneHint')
          }
        />

        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isPending}
            sx={{ minWidth: 160 }}
          >
            {mutation.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

// ── Provider info ───────────────────────────────────────────────────────

function ProviderInfoForm({
  providerData,
  onSaved,
}: {
  providerData: NonNullable<Awaited<ReturnType<typeof providersService.getMine>>>;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { translate } = useApiError();
  const { notify } = useNotification();

  const [businessName, setBusinessName] = useState(providerData.businessName);
  const [bio, setBio] = useState(providerData.bio ?? '');
  const [whatsapp, setWhatsapp] = useState(providerData.whatsappNumber ?? '');
  const [location, setLocation] = useState<LocationValue | null>(
    providerData.countryCode
      ? {
          countryCode: providerData.countryCode,
          countryName: providerData.countryName ?? '',
          stateCode: providerData.stateCode ?? undefined,
          stateName: providerData.stateName ?? undefined,
          cityName: providerData.cityName ?? undefined,
          latitude: providerData.latitude ?? undefined,
          longitude: providerData.longitude ?? undefined,
        }
      : null,
  );

  const mutation = useMutation({
    mutationFn: () =>
      providersService.updateMine({
        businessName: businessName.trim(),
        bio: bio.trim(),
        whatsappNumber: whatsapp.trim(),
        ...(location
          ? {
              countryCode: location.countryCode,
              countryName: location.countryName,
              stateCode: location.stateCode,
              stateName: location.stateName,
              cityName: location.cityName,
              latitude: location.latitude,
              longitude: location.longitude,
            }
          : {}),
      }),
    onSuccess: onSaved,
    onError: (err) => notify.error(translate(err)),
  });

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} noValidate>
      <Stack spacing={2}>
        <TextField
          label={t('provider.businessName')}
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          required
          fullWidth
          inputProps={{ maxLength: 160 }}
        />

        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          {t('provider.location')}
        </Typography>
        <LocationPicker value={location} onChange={setLocation} />

        <TextField
          label={t('provider.bio')}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          inputProps={{ maxLength: 5000 }}
          helperText={`${bio.length} / 5000`}
        />

        <TextField
          label={t('provider.whatsappNumber')}
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          fullWidth
          placeholder="+33 6 00 00 00 00"
        />

        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isPending}
            sx={{ minWidth: 160 }}
          >
            {mutation.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

// ── Social links ────────────────────────────────────────────────────────
//
// Public-shop social presence — small, sober icon strip rendered on the
// provider's profile. Each row accepts a full URL; the form pre-fills
// with whatever the backend currently holds and only PUTs the fields
// the user actually changed (using "" to clear).

type SocialKey =
  | 'instagram'
  | 'tiktok'
  | 'facebook'
  | 'snapchat'
  | 'youtube'
  | 'twitter';

const SOCIAL_FIELDS: ReadonlyArray<{
  key: SocialKey;
  // Maps to the providersService.updateMine payload key.
  payloadKey:
    | 'socialInstagram'
    | 'socialTiktok'
    | 'socialFacebook'
    | 'socialSnapchat'
    | 'socialYoutube'
    | 'socialTwitter';
  label: string;
  placeholder: string;
  icon: React.ReactNode;
}> = [
  {
    key: 'instagram',
    payloadKey: 'socialInstagram',
    label: 'Instagram',
    placeholder: 'https://instagram.com/your-handle',
    icon: <InstagramIcon fontSize="small" />,
  },
  {
    key: 'tiktok',
    payloadKey: 'socialTiktok',
    label: 'TikTok',
    placeholder: 'https://tiktok.com/@your-handle',
    icon: <MusicNoteIcon fontSize="small" />,
  },
  {
    key: 'facebook',
    payloadKey: 'socialFacebook',
    label: 'Facebook',
    placeholder: 'https://facebook.com/your-page',
    icon: <FacebookIcon fontSize="small" />,
  },
  {
    key: 'snapchat',
    payloadKey: 'socialSnapchat',
    label: 'Snapchat',
    placeholder: 'https://snapchat.com/add/your-handle',
    icon: <ChatBubbleOutlineRoundedIcon fontSize="small" />,
  },
  {
    key: 'youtube',
    payloadKey: 'socialYoutube',
    label: 'YouTube',
    placeholder: 'https://youtube.com/@your-channel',
    icon: <YouTubeIcon fontSize="small" />,
  },
  {
    key: 'twitter',
    payloadKey: 'socialTwitter',
    label: 'X / Twitter',
    placeholder: 'https://x.com/your-handle',
    icon: <XIcon fontSize="small" />,
  },
];

function SocialLinksForm({
  providerData,
  onSaved,
}: {
  providerData: NonNullable<Awaited<ReturnType<typeof providersService.getMine>>>;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { translate } = useApiError();
  const { notify } = useNotification();

  const initial: Record<SocialKey, string> = {
    instagram: providerData.social?.instagram ?? '',
    tiktok:    providerData.social?.tiktok ?? '',
    facebook:  providerData.social?.facebook ?? '',
    snapchat:  providerData.social?.snapchat ?? '',
    youtube:   providerData.social?.youtube ?? '',
    twitter:   providerData.social?.twitter ?? '',
  };
  const [values, setValues] = useState<Record<SocialKey, string>>(initial);

  const set = (k: SocialKey, v: string) =>
    setValues((prev) => ({ ...prev, [k]: v }));

  const mutation = useMutation({
    mutationFn: () => {
      // Only send the fields the user actually changed — keeps the PUT
      // body small and avoids no-op writes for untouched values.
      const payload: Partial<
        Record<(typeof SOCIAL_FIELDS)[number]['payloadKey'], string>
      > = {};
      for (const field of SOCIAL_FIELDS) {
        const current = values[field.key].trim();
        if (current !== (initial[field.key] ?? '').trim()) {
          payload[field.payloadKey] = current;
        }
      }
      return providersService.updateMine(payload);
    },
    onSuccess: onSaved,
    onError: (err) => notify.error(translate(err)),
  });

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      noValidate
    >
      <Typography color="text.secondary" sx={{ mb: 2, fontSize: '0.9375rem' }}>
        {t('account.social.hint')}
      </Typography>

      <Stack spacing={2}>
        {SOCIAL_FIELDS.map((field) => (
          <TextField
            key={field.key}
            label={field.label}
            value={values[field.key]}
            onChange={(e) => set(field.key, e.target.value)}
            placeholder={field.placeholder}
            fullWidth
            inputProps={{ maxLength: 2048, inputMode: 'url' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                  {field.icon}
                </InputAdornment>
              ),
            }}
          />
        ))}

        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isPending}
            sx={{ minWidth: 160 }}
          >
            {mutation.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

// ── Password ────────────────────────────────────────────────────────────

function PasswordForm() {
  const { t } = useTranslation();
  const { translate } = useApiError();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});

  const mutation = useMutation({
    mutationFn: () =>
      authService.changePassword(form.currentPassword, form.newPassword),
    onSuccess: () => {
      notify.success(t('account.password.successAndRelogin'));
      // The backend revokes all sessions on a password change — punt to
      // login so the user re-authenticates with the new password.
      setTimeout(() => navigate('/login', { replace: true }), 800);
    },
    onError: (err) => notify.error(translate(err)),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!form.currentPassword) next.currentPassword = t('auth.errors.passwordRequired');
    const newPasswordKey = validators.password(form.newPassword);
    if (newPasswordKey) next.newPassword = t(newPasswordKey);
    if (form.newPassword !== form.confirmPassword) {
      next.confirmPassword = t('auth.passwordMismatch');
    }
    if (form.currentPassword && form.newPassword === form.currentPassword) {
      next.newPassword = t('account.password.mustDiffer');
    }
    setErrors(next);
    if (Object.keys(next).length === 0) mutation.mutate();
  };

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <PasswordField
          label={t('account.password.current')}
          value={form.currentPassword}
          onChange={(e) => setForm((f) => ({ ...f, currentPassword: e.target.value }))}
          required
          fullWidth
          autoComplete="current-password"
          error={!!errors.currentPassword}
          helperText={errors.currentPassword}
        />
        <PasswordField
          label={t('account.password.new')}
          value={form.newPassword}
          onChange={(e) => setForm((f) => ({ ...f, newPassword: e.target.value }))}
          required
          fullWidth
          autoComplete="new-password"
          error={!!errors.newPassword}
          helperText={errors.newPassword || t('auth.passwordHint')}
        />
        <PasswordField
          label={t('auth.confirmPassword')}
          value={form.confirmPassword}
          onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
          required
          fullWidth
          autoComplete="new-password"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
        />
        <Alert severity="info">{t('account.password.willBeLoggedOut')}</Alert>
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isPending}
            sx={{ minWidth: 160 }}
          >
            {mutation.isPending ? t('common.loading') : t('account.password.submit')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

// ── Delete account ──────────────────────────────────────────────────────
//
// Self-service soft-delete. The submit button stays disabled until the
// user has (a) entered their password if they have one — OAuth-only
// accounts skip that — and (b) typed the localised confirmation word
// exactly. On success the backend revokes every session: we clear our
// local tokens and bounce to "/" so the next render lands on the public
// home page instead of an AuthGuard redirect to /login.

function DeleteAccountForm({ hasPassword }: { hasPassword: boolean }) {
  const { t } = useTranslation();
  const { translate } = useApiError();
  const { notify } = useNotification();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const confirmWord = t('account.delete.typeToConfirmWord');
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const mutation = useMutation({
    mutationFn: () => usersService.deleteMe(hasPassword ? password : ''),
    onSuccess: async () => {
      notify.success(t('account.delete.success'));
      // Backend already revoked every session as part of the soft-delete.
      // Drop cached queries and run the shared logout cleanup (clears
      // tokens + the cached user; the auth:logout listener inside
      // useAuth covers other tabs). The /auth/logout call inside
      // useAuth.logout() may 401 — the finally block still cleans up.
      qc.clear();
      await logout();
      navigate('/', { replace: true });
    },
    onError: (err) => {
      const msg = translate(err);
      // Surface the dedicated password-mismatch label when the backend
      // rejects the credentials, so the user knows what to fix.
      notify.error(
        /password/i.test(msg) ? t('account.delete.errorPassword') : msg,
      );
    },
  });

  const passwordOk = !hasPassword || password.length > 0;
  const confirmOk = confirmText.trim() === confirmWord;
  const canSubmit = passwordOk && confirmOk && !mutation.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  };

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <Typography variant="h5" component="h2" color="error.main">
          {t('account.delete.heading')}
        </Typography>

        <Alert severity="warning">{t('account.delete.intro')}</Alert>

        <Typography variant="body2" color="text.secondary">
          <Trans
            i18nKey="account.delete.retained"
            components={{
              docsLink: (
                <Link
                  component={RouterLink}
                  to="/account-deletion"
                  target="_blank"
                  rel="noopener"
                />
              ),
            }}
          />
        </Typography>

        {hasPassword ? (
          <PasswordField
            label={t('account.delete.passwordLabel')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            autoComplete="current-password"
            helperText={t('account.delete.passwordHelp')}
          />
        ) : (
          <Alert severity="info">{t('account.delete.oauthNotice')}</Alert>
        )}

        <TextField
          label={t('account.delete.typeToConfirmLabel')}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          fullWidth
          required
          inputProps={{ autoCapitalize: 'characters', spellCheck: false }}
          placeholder={confirmWord}
          helperText={t('account.delete.typeToConfirmHelp')}
          error={confirmText.length > 0 && !confirmOk}
        />

        <Box>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={!canSubmit}
            sx={{ minWidth: 200 }}
          >
            {mutation.isPending
              ? t('account.delete.submitting')
              : t('account.delete.submit')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
